import React from "react";
import PropTypes from "prop-types";

import ReactHtmlParser from "react-html-parser";
import DOMPurify from "dompurify";

import { GridHolder } from "../../GridHolder";
import JsonLdArticle from "../../../atoms/JsonLdArticle";
import PageMeta from "../../PageMeta";
import red from "../../../../assets/images/quiz/wine-red.png"; // same red wine glass as in quiz.
import aromatic from "../../../../assets/images/quiz/Asset169.png"; // same aromatic as in quiz.
import racy from "../../../../assets/images/quiz/Asset167.png";
import vegan from "../../../../assets/images/quiz/Asset153.png";

// cellar used in My Account is different (svg).
import cellar from "../../../../assets/images/guides/priorities/cellar.png";
import plum from "../../../../assets/images/guides/priorities/04_plum.png"; // plum used in quiz is different.
import earth from "../../../../assets/images/guides/priorities/earth-02-02.png";
import wines from "../../../../assets/images/guides/priorities/wines.png";
import lemon from "../../../../assets/images/temp/TasteChallengeImage1.svg";
import eggplant from "../../../../assets/images/guides/priorities/eggplant.png";
import pizza from "../../../../assets/images/guides/priorities/pizza-slice.png";
import burger from "../../../../assets/images/guides/priorities/burger.png";
import lavender from "../../../../assets/images/guides/priorities/lavender.png";
import cork from "../../../../assets/images/guides/priorities/cork.png";
import strawberry from "../../../../assets/images/guides/priorities/strawberry-guides.png";

import { extractNumberFromString, extractIdFromYoutubeUrl } from "../../../../helpers/tools";
import { BREAKPOINT } from "../../../../styles/variables";
import GuidesRelatedPosts from "../../GuidesRelatedPosts";
import { GUIDES_HEADER_REPLACEMENTS_FOR_SEO } from "../../../../helpers/constants";
import YoutubeVideo from "../../../organisms/YoutubeVideo";
import {
  NavItem,
  SubHeaderContainer,
  LegendContainer,
  StyledDiv,
  HrDiv,
  LegendsGridHolder,
  ContentGridHolder,
  StickyDiv,
  StyledGridHolderItemContent,
  ContentsHeaderTitle,
  TitleDiv,
  SubHeaderIcon,
  LegendsIconContainer,
  LegendsIcon,
  PageTitle,
  SubHeaderTitle,
  VideoContainer,
} from "./style";

const guidesDetailIconMapper = {
  red,
  plum,
  aromatic,
  racy,
  vegan,
  cellar,
  earth,
  wines,
  lemon,
  eggplant,
  pizza,
  burger,
  lavender,
  cork,
  strawberry,
};

const smUpHeaderHeight = 400; // title font: 75px
const smDownHeaderHeight = 275; // title font: 45px

const GuidesDetail = ({ isHeaderShown = true, location = {}, ...props }) => {
  const refs = [];

  /**
   * Splits the data that's read for subheader node, and splits the title and icon accordingly;
   *
   * @param {string} data
   * @return {Object}
   * */
  const splitTitleDataToTextAndIcon = (data = "") => {
    const [title, iconName] = data.split("--") || [];
    const icon = iconName && iconName.trim();
    return { title, icon };
  };

  /**
   * This function transforms a html dom element i.e. node
   *
   * @param {Object} node
   * @param {Number} index
   * @returns {Component || undefined}
   * */
  const transform = (node, index) => {
    const { name, children } = node || {};
    if (`${name}` === "h1" || `${name}` === "h2") {
      const [header] = children || [];
      const { data } = header || {};
      if (data) {
        refs.push(data);
        const { title, icon } = splitTitleDataToTextAndIcon(data) || {};
        return (
          <SubHeaderContainer key={`${index}`} id={`${refs.length}`}>
            {icon && <SubHeaderIcon src={guidesDetailIconMapper[icon]} />}
            <SubHeaderTitle>{title}</SubHeaderTitle>
          </SubHeaderContainer>
        );
      }

      // returning undefined in this parsing transform function doesnt transform the DOM node.
      // And renders the HTML element as it is.
      return undefined;
    }
    if (`${name}` === "iframe") {
      const { attribs } = node || {};
      const { src: iFrameSrc } = attribs || {};

      const isValidYoutubeLink = extractIdFromYoutubeUrl(iFrameSrc || "");
      if (isValidYoutubeLink) {
        return (
          <VideoContainer key={iFrameSrc}>
            <YoutubeVideo videoUrl={iFrameSrc} videoAspectRatio={16 / 9} />
          </VideoContainer>
        );
      }
      return <VideoContainer key="Empty container" />;
    }
    if (`${name}` === "hr") {
      return <HrDiv />;
    }
    return undefined;
  };

  /**
   * Handles the scroll when a sub-header is clicked.
   *
   * @param {Number} id
   * */
  const handleSubHeaderClick = (id) => {
    if (!document || !window) return;

    const el = document.getElementById(id.toString());
    if (!el) return;

    const windowWidth = extractNumberFromString(`${window.innerWidth}`);

    const headerComponentHeight = windowWidth > BREAKPOINT.SM ? smUpHeaderHeight : smDownHeaderHeight;

    // The offset to handle the in-page links.
    const topOffSet = windowWidth > BREAKPOINT.MD ? headerComponentHeight : 0;

    window.scrollTo({
      behavior: "smooth",
      top: el.offsetTop + topOffSet,
    });
  };

  /**
   * Renders Sub-Headers legend for easy navigation to different sections of the guide details page.
   *
   * @param {String} item
   * @param {Number} index
   * @returns {Component}
   * */
  const renderSubHeaders = (item, index) => {
    const { title, icon } = splitTitleDataToTextAndIcon(item) || {};
    return (
      <NavItem onClick={() => handleSubHeaderClick(index + 1)} key={`subHeader-${index}`}>
        {!!icon && (
          <LegendsIconContainer>
            <LegendsIcon src={guidesDetailIconMapper[icon]} />
          </LegendsIconContainer>
        )}
        <span>{title}</span>
      </NavItem>
    );
  };

  const { post } = props || {};
  // eslint-disable-next-line camelcase
  const { html, meta_title, meta_description } = post || {};

  // Attributes added to to follow Google's Microdata guidelines on FAQs.
  // https://developers.google.com/search/docs/data-types/faqpage
  const purifiedHtml = DOMPurify.sanitize(html, {
    ADD_ATTR: ["itemscope", "itemtype", "itemprop"],
    ADD_TAGS: ["iframe"],
  });

  const parsedHtml = ReactHtmlParser(purifiedHtml, { transform });
  // eslint-disable-next-line camelcase
  const metaTitle = meta_title || post.title;
  // eslint-disable-next-line camelcase
  const metaDescription = meta_description || post.excerpt || "";

  const hasSubHeaders = refs && refs.length > 1;

  // For some hand picked guides articles, we change the h1 title for better SEO,
  // however the title remains the same in glossary page or the title above guides tab bar.
  const articleTitle = GUIDES_HEADER_REPLACEMENTS_FOR_SEO[post.slug]
    ? GUIDES_HEADER_REPLACEMENTS_FOR_SEO[post.slug]
    : post.title;

  return (
    <>
      <PageMeta title={metaTitle} description={metaDescription} location={location} />
      <JsonLdArticle name={metaTitle || post.title} description={metaDescription || post.excerpt || ""} />
      <>
        <GridHolder>
          {hasSubHeaders && (
            <LegendsGridHolder>
              <StickyDiv isHeaderShown={isHeaderShown}>
                <ContentsHeaderTitle>CONTENTS</ContentsHeaderTitle>
                <LegendContainer>{refs.map((item, index) => renderSubHeaders(item, index))}</LegendContainer>
              </StickyDiv>
            </LegendsGridHolder>
          )}

          <ContentGridHolder hasSubHeaders={hasSubHeaders}>
            <StyledGridHolderItemContent>
              <TitleDiv addPaddingTop={false}>
                <PageTitle fontSecondary>{articleTitle}</PageTitle>
              </TitleDiv>
              <StyledDiv>{parsedHtml}</StyledDiv>
            </StyledGridHolderItemContent>
            <GuidesRelatedPosts post={post} location={location} />
          </ContentGridHolder>
        </GridHolder>
      </>
    </>
  );
};

GuidesDetail.propTypes = {
  post: PropTypes.shape({}).isRequired,
  isHeaderShown: PropTypes.bool,
  location: PropTypes.shape({}),
};

export default GuidesDetail;
